import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ProductSlider.css";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, incrementQty, decrementQty } from "../redux/cartSlice";
import { useNavigate } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import { useModalDialog } from "../hooks/useModalDialog";
import { API_BASE_URL } from "../config/apiConfig";
import { getRealImageUrl } from "../utils/getProductImage";
import BulbCutout from "../assets/products/topseller-cutouts/3.png";
import DownlightCutout from "../assets/products/topseller-cutouts/9.png";
import PanelCutout from "../assets/products/topseller-cutouts/28.png";
import FloodCutout from "../assets/products/topseller-cutouts/31.png";
import RopeCutout from "../assets/products/topseller-cutouts/35.png";

/* Presentation-only transparent cutouts for this section's showcase
   cards, used only when the product has no real Admin-Panel image of
   its own (see getRealImageUrl below, which always takes priority).
   Mirrors the keyword logic in utils/getProductImage.js — kept local so
   the shared image-mapping util (used everywhere else) stays untouched. */
const TOPSELLER_CUTOUT_MAP = [
  { keywords: ["bulb", "candle"], image: BulbCutout },
  { keywords: ["downlight", "down light", "cob", "smd", "ice light"], image: DownlightCutout },
  { keywords: ["batten", "tube"], image: DownlightCutout },
  { keywords: ["panel"], image: PanelCutout },
  { keywords: ["flood", "street"], image: FloodCutout },
  { keywords: ["rope", "strip", "decorative"], image: RopeCutout },
];

function getTopsellerCutout(item) {
  if (!item) return BulbCutout;

  // A real uploaded image always wins over the local cutout fallback.
  const realUrl = getRealImageUrl(item);
  if (realUrl) return realUrl;

  const haystack = [item.category, item.subcategory, item.name, item.title, item.sub, item.product]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const match = TOPSELLER_CUTOUT_MAP.find(({ keywords }) =>
    keywords.some((kw) => haystack.includes(kw))
  );

  return match ? match.image : BulbCutout;
}

const FEATURE_STRIP = [
  {
    title: "1-Year Warranty",
    text: "Peace of mind, always.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l8 3v6c0 5-3.4 9-8 11-4.6-2-8-6-8-11V5z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    title: "Energy Efficient",
    text: "Save up to 85% electricity.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 3C7 5 5 8 5 12a7 7 0 0 0 14 0c0-1.5-.5-3-1.5-4.5C16 9 14 9 13 7c-.8-1.5-1-2.7-2-4z" />
      </svg>
    ),
  },
  {
    title: "Nationwide Delivery",
    text: "Fast & reliable across Pakistan.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 7h11v8H2z" /><path d="M13 10h4l3 3v2h-7z" />
        <circle cx="6" cy="18" r="1.7" /><circle cx="17" cy="18" r="1.7" />
      </svg>
    ),
  },
  {
    title: "Dedicated Support",
    text: "We're here to help you.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 13a8 8 0 0 1 16 0" />
        <path d="M4 13v4a2 2 0 0 0 2 2h1v-6H5a1 1 0 0 0-1 1z" />
        <path d="M20 13v4a2 2 0 0 1-2 2h-1v-6h1a1 1 0 0 1 2 1z" />
        <path d="M14 19a2 2 0 0 1-4 0" />
      </svg>
    ),
  },
];

const ProductSlider = () => {
  const [products, setProducts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const reducedMotion = useReducedMotion();
  const { dialogRef } = useModalDialog(Boolean(selectedProduct), () => setSelectedProduct(null));

  const trackRef = useRef(null);
  const timerRef = useRef(null);
  const touchStartX = useRef(0);

  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const navigate = useNavigate();

  const autoplayDelay = 4200;

  // Fetch products from adminhlc-api (MySQL) — Step 5 of the Firebase->PHP migration
  // ROLLBACK: original Firebase implementation:
  // useEffect(() => {
  //   const productsRef = dbRef(db, "products");
  //   const unsubscribe = onValue(productsRef, (snapshot) => {
  //     const data = snapshot.val();
  //     if (data) {
  //       const allProducts = Object.keys(data).map((key) => ({
  //         id: key,
  //         ...data[key],
  //       }));
  //       const filtered = allProducts.filter((p) => p.topSelling === true);
  //       setProducts(filtered);
  //     } else {
  //       setProducts([]);
  //     }
  //     setLoading(false);
  //   });
  //   return () => unsubscribe();
  // }, []);
  useEffect(() => {
    let cancelled = false;

    fetch(`${API_BASE_URL}/api/public/products/list.php`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const filtered = Array.isArray(data)
          ? data.filter((p) => p.topSelling === true)
          : [];
        setProducts(filtered);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch products:", err);
        if (!cancelled) {
          setProducts([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const getCartItem = (id) => cartItems.find((item) => item.id === id);

  const getVisibleCards = () => {
    const w = window.innerWidth;
    if (w <= 768) return 2;
    if (w <= 1100) return 3;
    return 4;
  };

  const [visibleCards, setVisibleCards] = useState(
    typeof window !== "undefined" ? getVisibleCards() : 4
  );

  const maxIndex = useMemo(
    () => Math.max(products.length - visibleCards, 0),
    [products.length, visibleCards]
  );

  const getCardStep = () => {
    const track = trackRef.current;
    if (!track || !track.children.length) return 0;

    const firstCard = track.children[0];
    const cardRect = firstCard.getBoundingClientRect();
    const trackStyle = window.getComputedStyle(track);
    const gap = parseFloat(trackStyle.columnGap || trackStyle.gap || 0);

    return cardRect.width + gap;
  };

  const stopAutoplay = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startAutoplay = () => {
    stopAutoplay();
    if (reducedMotion || selectedProduct) return;

    if (maxIndex > 0) {
      timerRef.current = setInterval(() => {
        const bounds = trackRef.current?.getBoundingClientRect();
        if (document.hidden || !bounds || bounds.bottom <= 0 || bounds.top >= window.innerHeight) return;
        setCurrentIndex((prev) => {
          const nextIndex = prev >= maxIndex ? 0 : prev + 1;
          const track = trackRef.current;

          if (track) {
            track.scrollTo({
              left: nextIndex * getCardStep(),
              behavior: "smooth",
            });
          }

          return nextIndex;
        });
      }, autoplayDelay);
    }
  };

  const restartAutoplay = () => {
    stopAutoplay();
    startAutoplay();
  };

  const goTo = (index, manual = false) => {
    const track = trackRef.current;
    if (!track) return;

    const clamped = Math.max(0, Math.min(index, maxIndex));
    setCurrentIndex(clamped);

    track.scrollTo({
      left: clamped * getCardStep(),
      behavior: reducedMotion ? "instant" : "smooth",
    });

    if (manual) restartAutoplay();
  };

  const nextSlide = (manual = false) => {
    const next = currentIndex >= maxIndex ? 0 : currentIndex + 1;
    goTo(next, manual);
  };

  const prevSlide = (manual = false) => {
    const prev = currentIndex <= 0 ? maxIndex : currentIndex - 1;
    goTo(prev, manual);
  };

  const viewProduct = (productId) => navigate(`/product/${productId}`);

  const openQuickView = (product, e) => {
    e.stopPropagation();
    setSelectedProduct(product);
  };

  const closeQuickView = () => setSelectedProduct(null);

  useEffect(() => {
    const handleResize = () => setVisibleCards(getVisibleCards());
    const handleEscape = (e) => {
      if (e.key === "Escape") closeQuickView();
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    const safeIndex = Math.min(currentIndex, maxIndex);

    setCurrentIndex(safeIndex);

    const timeout = setTimeout(() => {
      goTo(safeIndex, false);
      startAutoplay();
    }, 100);

    return () => {
      clearTimeout(timeout);
      stopAutoplay();
    };
  }, [visibleCards, maxIndex, products.length]);

  useEffect(() => {
    startAutoplay();
    return () => stopAutoplay();
  }, [currentIndex, maxIndex, products.length, reducedMotion, selectedProduct]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const endX = e.changedTouches[0].clientX;
    const diff = endX - touchStartX.current;

    if (Math.abs(diff) > 40) {
      if (diff < 0) nextSlide(true);
      else prevSlide(true);
    }
  };

  return (
    <section id="topseller-slider" aria-label="Top Selling Products">
      <div className="topseller-wrap">
        <header className="topseller-head">
          <p className="topseller-eyebrow">Best Sellers</p>
          <p className="topseller-kicker">Lighting people choose</p>
          <h2 className="topseller-heading">
            Top Selling <span className="topseller-heading__accent">Products</span>.
          </h2>
          <p className="topseller-subtext">
            Explore HLC's most-loved lighting solutions, designed for brighter
            spaces and smarter energy use.
          </p>
        </header>

        {loading ? (
          <div className="topseller-loaderWrap">
            <div className="topseller-loader"></div>
          </div>
        ) : products.length === 0 ? (
          <p className="topseller-emptyText">No Top Selling products available.</p>
        ) : (
          <>
            <div className="topseller-shell">
              <button
                className="topseller-nav topseller-prev"
                type="button"
                aria-label="Previous"
                onClick={() => prevSlide(true)}
              >
                ‹
              </button>

              <div
                className="topseller-viewport"
                onMouseEnter={stopAutoplay}
                onMouseLeave={startAutoplay}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <div className="topseller-track" ref={trackRef}>
                  {products.map((p) => {
                    const cartItem = getCartItem(p.id);
                    const image = getTopsellerCutout(p);

                    return (
                      <article
                        className="topseller-card"
                        key={p.id}
                        onClick={() => viewProduct(p.id)}
                      >
                        <div className="topseller-badge topseller-badge--top">
                          Best Seller
                        </div>

                        <figure className="topseller-figure">
                          <img src={image} alt={p.name} loading="lazy" />
                          <button
                            className="topseller-qv"
                            type="button"
                            aria-label="Quick view"
                            onClick={(e) => openQuickView(p, e)}
                          >
                            +
                          </button>
                        </figure>

                        <div className="topseller-body">
                          <h3 className="topseller-title" title={p.name}>{p.name}</h3>

                          <p className="topseller-desc">
                            {p.category || p.description || "Lighting Product"}
                          </p>

                          <p className="topseller-price">
                            {p.discountedPrice && <del>₨ {p.price}</del>} ₨{" "}
                            {p.discountedPrice || p.price}
                          </p>

                          <div className="topseller-cta">
                            {!cartItem ? (
                              <button
                                className="topseller-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dispatch(addToCart(p));
                                }}
                              >
                                <svg
                                  className="topseller-btn__icon"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M6 7h12l-1 13H7L6 7z" />
                                  <path d="M9 7a3 3 0 0 1 6 0" />
                                </svg>
                                Add to Cart
                              </button>
                            ) : (
                              <div
                                className="topseller-counter"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button onClick={() => dispatch(decrementQty(p.id))}>
                                  -
                                </button>
                                <span>{cartItem.quantity || 1}</span>
                                <button onClick={() => dispatch(incrementQty(p.id))}>
                                  +
                                </button>
                              </div>
                            )}

                            <button
                              className="topseller-shopLink"
                              onClick={(e) => {
                                e.stopPropagation();
                                viewProduct(p.id);
                              }}
                            >
                              Shop Now →
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>

              <button
                className="topseller-nav topseller-next"
                type="button"
                aria-label="Next"
                onClick={() => nextSlide(true)}
              >
                ›
              </button>
            </div>

            <div className="topseller-dots" aria-label="Slider dots">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  className={i === currentIndex ? "is-active" : ""}
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => goTo(i, true)}
                />
              ))}
            </div>
          </>
        )}

        <div className="topseller-viewAllWrap">
          <button
            className="topseller-viewAllBtn"
            onClick={() => navigate("/shop")}
          >
            VIEW ALL PRODUCTS →
          </button>
        </div>
      </div>

      <div className="topseller-features">
        <div className="topseller-features__grid">
          {FEATURE_STRIP.map((f) => (
            <div className="topseller-feature" key={f.title}>
              <span className="topseller-feature__icon" aria-hidden="true">
                {f.icon}
              </span>
              <div>
                <h4>{f.title}</h4>
                <p>{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedProduct && (
        <div className="topseller-modal">
          <div
            className="topseller-modal__scrim"
            onClick={closeQuickView}
          ></div>

          <div
            className="topseller-modal__dialog"
            ref={dialogRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Quick View"
          >
            <button
              className="topseller-modal__close"
              type="button"
              aria-label="Close"
              onClick={closeQuickView}
            >
              ✕
            </button>

            <div className="topseller-modal__body">
              <div className="topseller-qv__content">
                <div className="topseller-qv__media">
                  <img
                    src={getTopsellerCutout(selectedProduct)}
                    alt={selectedProduct.name}
                  />
                </div>

                <div className="topseller-qv__info">
                  <h3>{selectedProduct.name}</h3>

                  <p className="topseller-qv__price">
                    {selectedProduct.discountedPrice && (
                      <del>₨ {selectedProduct.price}</del>
                    )}{" "}
                    <strong>
                      ₨{" "}
                      {selectedProduct.discountedPrice || selectedProduct.price}
                    </strong>
                  </p>

                  <ul className="topseller-qv__bullets">
                    <li>{selectedProduct.category || "Lighting Product"}</li>
                    <li>
                      {selectedProduct.description ||
                        "Premium quality lighting solution"}
                    </li>
                    {selectedProduct.discount && (
                      <li>Offer: {selectedProduct.discount}</li>
                    )}
                  </ul>

                  <div className="topseller-qv__ctaRow">
                    <button
                      className="topseller-btn"
                      onClick={() => viewProduct(selectedProduct.id)}
                    >
                      View Details
                    </button>

                    {!getCartItem(selectedProduct.id) ? (
                      <button
                        className="topseller-btn topseller-btn--ghost"
                        onClick={() =>
                          dispatch(addToCart(selectedProduct))
                        }
                      >
                        <svg
                          className="topseller-btn__icon"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M6 7h12l-1 13H7L6 7z" />
                          <path d="M9 7a3 3 0 0 1 6 0" />
                        </svg>
                        Add to Cart
                      </button>
                    ) : (
                      <div className="topseller-counter">
                        <button
                          onClick={() =>
                            dispatch(decrementQty(selectedProduct.id))
                          }
                        >
                          -
                        </button>
                        <span>
                          {getCartItem(selectedProduct.id)?.quantity || 1}
                        </span>
                        <button
                          onClick={() =>
                            dispatch(incrementQty(selectedProduct.id))
                          }
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProductSlider;
